import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaStar, FaCamera } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api, { getImageUrl } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import useScrollReveal from '../hooks/useScrollReveal';

function UserReviews() {
  const { settings } = useSettings();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', content: '', rating: 5 });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sending, setSending] = useState(false);
  useScrollReveal();

  useEffect(() => {
    api.get('/testimonials')
      .then(res => setReviews(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('content', form.content);
      formData.append('rating', form.rating);
      if (image) formData.append('image', image);

      await api.post('/testimonials/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Yorumunuz başarıyla gönderildi! Onaylandıktan sonra burada görünecektir.');
      setForm({ name: '', content: '', rating: 5 });
      setImage(null);
      setImagePreview(null);
    } catch (err) {
      const msg = err.response?.data?.error || 'Yorum gönderilirken bir hata oluştu.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FaStar key={i} style={{ color: i < rating ? '#f5a623' : '#ddd', fontSize: 16 }} />
    ));
  };

  return (
    <>
      <Helmet>
        <title>Sizden Gelenler | {settings.site_title || ''}</title>
      </Helmet>

      <div className="page-header">
        <div className="container">
          <h1>Sizden Gelenler</h1>
          <div className="breadcrumb">
            <Link to="/">Ana Sayfa</Link>
            <span>/</span>
            <span>Sizden Gelenler</span>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {/* Review Form */}
          <div className="review-form-section reveal">
            <h2>Deneyiminizi Paylaşın</h2>
            <p className="review-form-desc">Hizmetlerimizle ilgili deneyiminizi bizimle paylaşın. Yorumunuz onaylandıktan sonra yayınlanacaktır.</p>
            <form className="review-form" onSubmit={handleSubmit}>
              <div className="review-form-grid">
                <div className="form-group">
                  <label>Adınız Soyadınız *</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Adınız Soyadınız"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Puanınız</label>
                  <div className="star-rating-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${parseInt(form.rating) >= star ? 'active' : ''}`}
                        onClick={() => setForm({ ...form, rating: star })}
                      >
                        <FaStar />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Yorumunuz *</label>
                <textarea
                  name="content"
                  placeholder="Hizmetlerimiz hakkında düşüncelerinizi yazın..."
                  rows="4"
                  value={form.content}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Fotoğraf (isteğe bağlı)</label>
                <div className="review-image-upload">
                  <label className="review-upload-btn">
                    <FaCamera /> Fotoğraf Seç
                    <input type="file" accept="image/*" onChange={handleImageChange} hidden />
                  </label>
                  {imagePreview && (
                    <img src={imagePreview} alt="Önizleme" className="review-image-preview" />
                  )}
                </div>
              </div>
              <button type="submit" className="btn" disabled={sending}>
                {sending ? 'Gönderiliyor...' : 'Yorum Gönder'}
              </button>
            </form>
          </div>

          {/* Approved Reviews */}
          <div className="reviews-section reveal">
            <h2>Müşteri Deneyimleri</h2>
            {loading ? (
              <p style={{ textAlign: 'center' }}>Yükleniyor...</p>
            ) : reviews.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#999' }}>Henüz onaylı yorum bulunmuyor. İlk yorumu siz yapın!</p>
            ) : (
              <div className="reviews-grid">
                {reviews.map((review) => (
                  <div key={review.id} className="review-card">
                    <div className="review-card-header">
                      {review.image_path ? (
                        <img src={getImageUrl(review.image_path)} alt={review.name} className="review-avatar" />
                      ) : (
                        <div className="review-avatar-placeholder">
                          {review.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <h4>{review.name}</h4>
                        <div className="review-stars">{renderStars(review.rating)}</div>
                      </div>
                    </div>
                    <p className="review-content">{review.content}</p>
                    {review.image_path && (
                      <img src={getImageUrl(review.image_path)} alt="" className="review-photo" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default UserReviews;
